import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, Plus, X, Trash2, Eye } from 'lucide-react';
import { HitApi } from '../../../Api/ApiHit';
import { searchBanners, addBanner, deleteBanner } from '../../../constant/Constant';
import FileuploadComp from '../../../components/SingleFileUpload/FileuploadComp';
import { setApiJson } from '../../../redux/Actions/ApiAction';
import { getSelectedUnit } from '../../../storage/Storage';

const BannerManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ApiReducer = useSelector(state => state.ApiReducer);

  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, [id]);

  const fetchBanners = async () => {
    try {
      setLoading(true);

      const requestPayload = {
        page: 1,
        limit: 100,
        search: {
          kioskId: id,
          isActive: true
        }
      };

      console.log('📤 Fetching banners:', requestPayload);

      const response = await HitApi(requestPayload, searchBanners);

      console.log('📡 Banners response:', response);

      if (response.success && response.data) {
        setBanners(response.data);
        console.log('✅ Banners loaded:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBanner = () => {
    // Reset form
    dispatch(setApiJson({
      title: '',
      order: 0,
      startDate: '',
      endDate: '',
      imageUrl: ''
    }));
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const imageUrl = ApiReducer?.apiJson?.imageUrl;
    const title = ApiReducer?.apiJson?.title;
    const order = ApiReducer?.apiJson?.order;
    const startDate = ApiReducer?.apiJson?.startDate;
    const endDate = ApiReducer?.apiJson?.endDate;

    if (!imageUrl) {
      alert('Please upload an image');
      return;
    }

    if (!title) {
      alert('Please enter a title');
      return;
    }

    try {
      setIsSubmitting(true);

      const selectedUnit = getSelectedUnit();
      const requestPayload = {
        kioskId: id,
        unitId: selectedUnit?._id,
        title: title,
        imageUrl: imageUrl,
        order: parseInt(order) || 0,
        isActive: true
      };

      // Add dates if provided
      if (startDate) {
        requestPayload.startDate = new Date(startDate).toISOString();
      }
      if (endDate) {
        requestPayload.endDate = new Date(endDate).toISOString();
      }

      console.log('📤 Adding banner:', requestPayload);

      const response = await HitApi(requestPayload, addBanner);

      console.log('📡 Add banner response:', response);

      if (response.success) {
        alert('Banner added successfully!');
        setShowAddModal(false);
        fetchBanners(); // Refresh the list
      } else {
        alert('Failed to add banner: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error adding banner:', error);
      alert('Failed to add banner');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    const currentJson = ApiReducer?.apiJson || {};
    dispatch(setApiJson({
      ...currentJson,
      [field]: value
    }));
  };

  const handleDeleteBanner = async (banner) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${banner.title}"? This action cannot be undone.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const requestPayload = {
        _id: banner._id
      };

      console.log('📤 Deleting banner:', requestPayload);

      const response = await HitApi(requestPayload, deleteBanner);

      console.log('📡 Delete banner response:', response);

      if (response.success) {
        alert('Banner deleted successfully!');
        fetchBanners(); // Refresh the list
      } else {
        alert('Failed to delete banner: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error deleting banner:', error);
      alert('Failed to delete banner');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 font-medium">Loading banners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/kiosk-management/dashboard/${id}`)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Banner Management</h1>
          </div>
          <button
            onClick={handleAddBanner}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Banner
          </button>
        </div>

        {/* Banners List */}
        {banners.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Banners Found</h3>
            <p className="text-gray-600 mb-4">No banners have been added for this kiosk yet.</p>
            <button
              onClick={handleAddBanner}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Your First Banner
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {banners.map((banner, index) => (
              <div key={banner._id || index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative">
                  <img
                    src={banner.imageUrl}
                    alt={banner.title}
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={() => handleDeleteBanner(banner)}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    title="Delete banner"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-2">{banner.title}</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Order: {banner.order}</p>
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      <span>Views: {banner.views || 0}</span>
                    </div>
                    {banner.startDate && <p>Start: {new Date(banner.startDate).toLocaleDateString()}</p>}
                    {banner.endDate && <p>End: {new Date(banner.endDate).toLocaleDateString()}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Banner Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Add New Banner</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={ApiReducer?.apiJson?.title || ''}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter banner title"
                    required
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <FileuploadComp
                    title="Banner Image *"
                    name="imageUrl"
                    allowed={["img"]}
                  />
                </div>

                {/* Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order
                  </label>
                  <input
                    type="number"
                    value={ApiReducer?.apiJson?.order || 0}
                    onChange={(e) => handleInputChange('order', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={ApiReducer?.apiJson?.startDate || ''}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={ApiReducer?.apiJson?.endDate || ''}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Banner'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BannerManagement;
